import { motion, useInView } from 'framer-motion';
import { useRef } from 'react';

const skillCategories = [
  {
    label: 'Languages',
    comment: '// what I write in',
    color: 'green',
    items: ['TypeScript', 'JavaScript', 'Solidity', 'HTML5', 'CSS3'],
  },
  {
    label: 'Frameworks',
    comment: '// tools I build with',
    color: 'blue',
    items: ['React', 'Next.js', 'Node.js', 'Express.js', 'Chakra UI', 'Tailwind CSS'],
  },
  {
    label: 'Tools & Infra',
    comment: '// how I ship',
    color: 'purple',
    items: ['Git', 'Docker', 'Foundry', 'MongoDB', 'Firebase', 'Figma', 'Vercel'],
  },
  {
    label: 'Concepts',
    comment: '// how I think',
    color: 'amber',
    items: ['REST APIs', 'Web3 / DApps', 'Smart Contracts', 'Responsive Design', 'Agile / Scrum'],
  },
];

const categoryStyles = {
  green: {
    border: 'hover:border-green-800/50',
    label: 'text-green-500',
    badge: 'bg-green-950/30 border-green-900/40 text-green-400 hover:border-green-600/60 hover:text-green-300',
  },
  blue: {
    border: 'hover:border-blue-800/50',
    label: 'text-blue-400',
    badge: 'bg-blue-950/30 border-blue-900/40 text-blue-300 hover:border-blue-600/60 hover:text-blue-200',
  },
  purple: {
    border: 'hover:border-purple-800/50',
    label: 'text-purple-400',
    badge: 'bg-purple-950/30 border-purple-900/40 text-purple-300 hover:border-purple-600/60 hover:text-purple-200',
  },
  amber: {
    border: 'hover:border-amber-800/50',
    label: 'text-amber-400',
    badge: 'bg-amber-950/30 border-amber-900/40 text-amber-300 hover:border-amber-600/60 hover:text-amber-200',
  },
};

const containerVariants = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.12 } },
};

const itemVariants = {
  hidden: { opacity: 0, y: 28 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.55, ease: 'easeOut' } },
};

const AboutSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-80px' });

  return (
    <section
      ref={ref}
      className="bg-[#050505] text-white px-6 md:px-12 dot-grid"
      style={{ paddingTop: '7rem', paddingBottom: '6rem', minHeight: '100vh' }}
    >
      <div className="max-w-5xl mx-auto">

        {/* ── Section Header ──────────────────────────── */}
        <motion.div
          className="flex items-center gap-4 mb-14 mt-6"
          initial={{ opacity: 0, x: -30 }}
          animate={isInView ? { opacity: 1, x: 0 } : {}}
          transition={{ duration: 0.6 }}
        >
          <span className="text-[#4d760c] font-mono text-sm">01.</span>
          <h2 className="text-3xl font-bold text-white">About Me</h2>
          <div className="flex-1 h-px bg-gradient-to-r from-[#2a2a2a] to-transparent" />
        </motion.div>

        {/* ── Bio + Highlights ────────────────────────── */}
        <motion.div
          className="grid md:grid-cols-5 gap-10 mb-20"
          variants={containerVariants}
          initial="hidden"
          animate={isInView ? 'visible' : 'hidden'}
        >
          {/* Bio text */}
          <motion.div className="md:col-span-3 space-y-5" variants={itemVariants}>
            <p className="text-slate-300 leading-relaxed">
              Hi! I'm a solution-driven{' '}
              <span className="text-[#6ea60f] font-medium">Fullstack Engineer</span>{' '}
              passionate about building innovative, scalable, and user-focused applications.
              I've sharpened my expertise in both frontend and backend technologies,
              working across diverse domains to deliver high-quality software.
            </p>
            <p className="text-slate-400 leading-relaxed">
              I've crafted seamless booking experiences at{' '}
              <span className="text-[#6ea60f]">FlyBoku</span>, deployed audited smart
              contracts at <span className="text-[#6ea60f]">AllDAO</span>, and built
              performant interfaces at <span className="text-[#6ea60f]">Treshub</span>.
              I'm driven by the intersection of great design and reliable engineering.
            </p>
            <p className="text-slate-400 leading-relaxed">
              When I'm not coding, I write technical articles for the dev community
              and explore the ever-evolving Web3 ecosystem.
            </p>

            {/* Quick facts */}
            <div className="pt-3 flex flex-col gap-2 font-mono text-sm">
              <p className="text-slate-500">
                <span className="text-[#4d760c] mr-2">▸</span>
                Currently open to new opportunities
              </p>
              <p className="text-slate-500">
                <span className="text-[#4d760c] mr-2">▸</span>
                Based in Lagos, Nigeria 🇳🇬 &mdash; open to remote globally
              </p>
              <p className="text-slate-500">
                <span className="text-[#4d760c] mr-2">▸</span>
                Proficient in both Web2 and Web3 stacks
              </p>
            </div>
          </motion.div>

          {/* Decorative stat box */}
          <motion.div
            className="md:col-span-2 flex justify-center md:justify-end items-start pt-2"
            variants={itemVariants}
          >
            <div className="relative w-52 h-52">
              {/* Corner brackets */}
              <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-[#4d760c10] to-transparent border border-[#4d760c20]" />
              <div className="absolute top-4 left-4 w-5 h-5 border-t-2 border-l-2 border-[#4d760c70] rounded-tl" />
              <div className="absolute top-4 right-4 w-5 h-5 border-t-2 border-r-2 border-[#4d760c70] rounded-tr" />
              <div className="absolute bottom-4 left-4 w-5 h-5 border-b-2 border-l-2 border-[#4d760c70] rounded-bl" />
              <div className="absolute bottom-4 right-4 w-5 h-5 border-b-2 border-r-2 border-[#4d760c70] rounded-br" />
              {/* Center content */}
              <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-4">
                <p className="text-6xl font-bold gradient-text mb-1">4+</p>
                <p className="text-xs text-slate-500 font-mono uppercase tracking-widest leading-relaxed">
                  years of<br />experience
                </p>
                <div className="mt-3 w-8 h-px bg-[#4d760c]" />
                <p className="mt-2 text-xs text-slate-600 font-mono">
                  10+ projects shipped
                </p>
              </div>
            </div>
          </motion.div>
        </motion.div>

        {/* ── Skills Section Header ───────────────────── */}
        <motion.div
          className="flex items-center gap-4 mb-10"
          initial={{ opacity: 0, x: -30 }}
          animate={isInView ? { opacity: 1, x: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.4 }}
        >
          <span className="text-[#4d760c] font-mono text-sm">02.</span>
          <h2 className="text-3xl font-bold text-white">Skills & Tools</h2>
          <div className="flex-1 h-px bg-gradient-to-r from-[#2a2a2a] to-transparent" />
        </motion.div>

        {/* ── Skills Grid ─────────────────────────────── */}
        <motion.div
          className="grid sm:grid-cols-2 gap-5"
          variants={containerVariants}
          initial="hidden"
          animate={isInView ? 'visible' : 'hidden'}
        >
          {skillCategories.map(({ label, comment, color, items }) => {
            const styles = categoryStyles[color];
            return (
              <motion.div
                key={label}
                className={`bg-[#0d0d0d] border border-[#1c1c1c] rounded-xl p-6 ${styles.border} transition-colors duration-300`}
                variants={itemVariants}
                whileHover={{ y: -3, transition: { duration: 0.2 } }}
              >
                <p className={`text-xs font-mono mb-1 ${styles.label}`}>{comment}</p>
                <h3 className="text-sm font-semibold text-slate-300 mb-4 uppercase tracking-wider">
                  {label}
                </h3>
                <div className="flex flex-wrap gap-2">
                  {items.map((skill) => (
                    <span
                      key={skill}
                      className={`text-xs px-3 py-1.5 rounded-full border font-mono transition-all duration-200 cursor-default ${styles.badge}`}
                    >
                      {skill}
                    </span>
                  ))}
                </div>
              </motion.div>
            );
          })}
        </motion.div>
      </div>
    </section>
  );
};

export default AboutSection;
